/* eslint-disable max-lines-per-function */
import React, { useState } from "react";
import { gql } from "graphql-tag";
import { ApolloProvider as ApolloHookProvider, useMutation } from "@apollo/client";
import { DialogContent, Typography, Grid } from "@mui/material";
import { Button, Modal } from "components/haven-design";
import { DialogActions } from "components/haven-design/modal/dialog";
import { useSnackbar } from "components/haven-design/snackbar";
import { TemplateSelector } from "components/send-messages/template-selector";
import { TemplateForm, SubmitValues } from "components/send-messages";
import { Viewer } from "components/auth/types";
import { apolloClient } from "data/graphql";
import {
  SendMessages,
  SendMessagesVariables,
  getTemplateGroup_templateGroup as Template,
} from "types/gql-types";

import { calculateMessageValues } from "utils/template";
import logger from "services/logger";
import { TableResult, useInterestsTable } from "../interests-table/table-context";
import { renderRecipients } from "./utils";
import { getScope, useBulkElasticQuery } from "./bulk-scope";


const SEND_MESSAGE = gql`
  mutation SendMessages(
    $interestListIds: [ID!]!
    $interestIds: [ID!]
    $elasticQuery: ElasticQuery
    $pageFrom: Int
    $pageTo: Int
    $emailEnabled: Boolean!
    $emailRespondToEnabled: Boolean
    $emailBody: String!
    $emailSubject: String!
    $letterEnabled: Boolean!
    $letterBody: String!
    $smsEnabled: Boolean!
    $smsBody: String!
    $emailBccAddress: String
    $smsPropertyUseCaseId: ID
  ) {
    sendMessages(
      interestListIds: $interestListIds
      interestIds: $interestIds
      elasticQuery: $elasticQuery
      pageFrom: $pageFrom
      pageTo: $pageTo
      emailEnabled: $emailEnabled
      emailBody: $emailBody
      emailRespondToEnabled: $emailRespondToEnabled
      emailSubject: $emailSubject
      letterEnabled: $letterEnabled
      letterBody: $letterBody
      smsEnabled: $smsEnabled
      smsBody: $smsBody
      emailBccAddress: $emailBccAddress
      smsPropertyUseCaseId: $smsPropertyUseCaseId
    )
  }
`;
type Props = {
  isOpen: boolean;
  onRequestClose(): void;
  recipients: TableResult[];
  viewer: Viewer;
  currentListId: string;

  /** The table's active sort — required for correct page-range scoping (HAV-162/164). */
  defaultSorting?: string;
}

type MessageProps = {
  interestListIds: [string];
  actionsButton?: boolean;
  closeMenu?: () => void;
  pageFrom: number | null;
  pageTo: number | null;
}

const getVariables = (
  { interestListIds, pageFrom, pageTo }: MessageProps,
  messageParams: SubmitValues,
) => ({
  interestListIds,
  pageFrom,
  pageTo,
  ...messageParams,
});

export const MessageModal: React.FC<Props> = (props) => {
  const { openSnackbar } = useSnackbar();
  const [template, setTemplate] = useState<Template | null>(null);
  const { currentListId, isOpen, onRequestClose, recipients, viewer } = props;
  const updateValues = calculateMessageValues(template);
  const [isSending, setIsSending] = useState(false);
  const [pendingSend, setPendingSend] = useState<{
    messageParams: SubmitValues;
    resetForm:() => void;
  } | null>(null);
  const [sendTemplateMessages] = useMutation<SendMessages, SendMessagesVariables>(SEND_MESSAGE, { refetchQueries: ["getActivityFeed"] });

  const {
    hasSelectedAllInterests,
    selectedPageRange,
    totalSelectedInterests,
  } = useInterestsTable();
  // Carries the table's active sort so page-range message sends hit the rows on
  // screen (HAV-164, same class as HAV-162) — see bulk-scope.ts.
  const elasticQuery = useBulkElasticQuery(props.defaultSorting);

  const performSend = (messageParams: SubmitValues) => {
    const withQuery = hasSelectedAllInterests || (
      selectedPageRange.from !== null || selectedPageRange.to !== null
    );

    const variables = {
      ...getVariables({
        interestListIds: [currentListId],
        pageFrom: selectedPageRange.from,
        pageTo: selectedPageRange.to,
      }, messageParams),
      ...getScope(withQuery, elasticQuery, recipients.map((recipient) => recipient.id)),
    };

    setIsSending(true);
    sendTemplateMessages({ variables })
      .then(() => {
        openSnackbar({ message: "Success", status: "success" });
      })
      .catch((err) => {
        openSnackbar({
          message: "Error sending bulk message. Please don't retry in case part of the batch has been sent. Contact support@havenconnect.com",
          status: "error",
          duration: 4000,
        });
        logger.sendError(err);
        throw new Error(err);
      })
      .finally(() => {
        setIsSending(false);
        onRequestClose();
      });
  };

  const onSubmit = (messageParams: SubmitValues, resetForm?: () => void) => {
    if (hasSelectedAllInterests) {
      // Hold off resetting the draft until the send is actually confirmed below —
      // canceling should leave the message intact to edit and resubmit.
      setPendingSend({ messageParams, resetForm: resetForm ?? (() => {}) });
      return;
    }
    resetForm?.();
    performSend(messageParams);
  };

  const cancelSendAll = () => setPendingSend(null);
  const confirmSendAll = () => {
    if (!pendingSend) return;
    const { messageParams, resetForm } = pendingSend;
    setPendingSend(null);
    resetForm();
    performSend(messageParams);
  };


  return (
    <Modal title="Send Messages:" isOpen={isOpen} onRequestClose={onRequestClose} fullScreen maxWidth="md">
      <DialogContent>
        <ApolloHookProvider client={apolloClient}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle1">
              To:&nbsp;
              {renderRecipients(
                recipients,
                // @ts-ignore
                totalSelectedInterests,
                selectedPageRange,
              )}&nbsp;
            </Typography>
          </Grid>
          <TemplateSelector
            onChange={(selectedTemplate: Template | null) => {
              if (selectedTemplate) setTemplate(selectedTemplate);
            }}
            viewer={viewer}
            currentTemplate={template}
          />
          <Grid size={{ xs: 12 }}>
            <TemplateForm
              viewer={viewer}
              isSending={isSending}
              handleSubmit={onSubmit}
              deferReset={hasSelectedAllInterests}
              template={template}
              onRequestClose={onRequestClose}
              currentListId={currentListId}
              {...updateValues}
            />
          </Grid>
        </ApolloHookProvider>
      </DialogContent>
      <Modal
        isOpen={pendingSend !== null}
        onRequestClose={cancelSendAll}
        fullWidth
        maxWidth="sm"
        title="Send to All Applicants">
        <DialogContent>
          <Typography variant="body2">
            {`This will send to ALL ${totalSelectedInterests} applicants `}
            in this search. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={cancelSendAll}>
            Cancel
          </Button>
          <Button color="primary" onClick={confirmSendAll}>
            Send
          </Button>
        </DialogActions>
      </Modal>
    </Modal>
  );
};
